import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';

import { Constants } from '../../common';

/** Owns the application colour mode, including the pre-login experience and CDK overlays. */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Light mode is the product default until a user explicitly chooses a theme. */
  private readonly defaultTheme = false;
  private isDark = this.defaultTheme;
  private readonly darkModeSubject = new BehaviorSubject<boolean>(this.defaultTheme);

  /** Emits the current mode immediately and whenever the application theme changes. */
  readonly darkModeChanges$ = this.darkModeSubject.pipe(distinctUntilChanged());

  initialize(userKey = 'anonymous'): boolean {
    this.isDark = this.getSavedTheme(userKey);
    this.apply();
    return this.isDark;
  }

  setDarkMode(isDark: boolean, userKey = 'anonymous'): void {
    this.isDark = isDark;
    this.saveTheme(userKey, isDark);
    this.apply();
  }

  toggle(userKey = 'anonymous'): boolean {
    this.setDarkMode(!this.isDark, userKey);
    return this.isDark;
  }

  get darkMode(): boolean {
    return this.isDark;
  }

  /** Applies the public (logged-out) default without changing any saved user preference. */
  applyDefaultTheme(): void {
    this.isDark = this.defaultTheme;
    this.apply();
  }

  private getSavedTheme(userKey: string): boolean {
    const saved = localStorage.getItem(Constants.DARK_MODE_STATE_KEY);
    if (!saved) return this.defaultTheme;

    try {
      const parsed: unknown = JSON.parse(saved);
      if (typeof parsed === 'boolean') return parsed;
      if (parsed && typeof parsed === 'object') {
        const value = (parsed as Record<string, unknown>)[userKey];
        return typeof value === 'boolean' ? value : this.defaultTheme;
      }
    } catch {
      return saved === 'true';
    }

    return this.defaultTheme;
  }

  private saveTheme(userKey: string, value: boolean): void {
    let preferences: Record<string, boolean> = {};
    try {
      const saved = JSON.parse(localStorage.getItem(Constants.DARK_MODE_STATE_KEY) || '{}');
      if (typeof saved === 'boolean') {
        // Preserve the prior application-wide setting while migrating it to a
        // per-user preference.
        preferences[userKey] = saved;
      } else if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
        preferences = saved as Record<string, boolean>;
      }
    } catch {
      // Replace malformed legacy storage with the new user-scoped preference.
    }
    preferences[userKey] = value;
    localStorage.setItem(Constants.DARK_MODE_STATE_KEY, JSON.stringify(preferences));
  }

  private apply(): void {
    document.documentElement.classList.toggle('dark-mode', this.isDark);
    document.body.classList.toggle('dark-mode', this.isDark);
    document.documentElement.style.colorScheme = this.isDark ? 'dark' : 'light';
    this.darkModeSubject.next(this.isDark);
  }
}
